/**
 * client/input/aimassist.ts — console aim assist (pure, DOM-free, unit-tested).
 *
 * OWNER: INPUT agent.
 * Port of T5 aim_assist/aim_assist.cpp + aim_target.cpp (research/controller.md §5; algorithms MED,
 * constants HIGH = T6 PC dvar defaults from research/data/dvars_t6mp.json):
 *  - AimTarget_*: enemy alive players, box ±aim_target_sentient_radius(10) x [0, j_helmet], visibility trace
 *    to the vis bone, sorted by world distance; AimAssist_UpdateScreenTargets projects the 8 box corners
 *    to clip space and sorts by crosshair distance (max 64).
 *  - AimAssist_CalcSlowdown (slowdown 0.4 hip / 0.5 ADS in a 90x90 region), AimAssist_ApplyAutoAim (ADS snap,
 *    inert for SnS: crossbow autoAimRange 0, b-knife can't ADS), AimAssist_ApplyAutoMelee (lunge yaw/dist +
 *    view pull, lerp 40, region 320x240, range = weapon meleeChargeRange or aim_automelee_range 100),
 *    AimAssist_ApplyLockOn (rotational tracking at 0.6 of the relative angular velocity).
 *  - Order per frame [CTL §5.7]: turn rates (incl. slowdown) -> auto-aim -> auto-melee -> lock-on.
 * Remote players come from ClientWorld.players, i.e. INTERPOLATED positions [NET §12.17].
 *
 * Space: CoD (Z-up, X forward, Y left), degrees, +yaw = turn left, +pitch = look down.
 */
import { DEG2RAD, RAD2DEG, angleVectors, type ReadonlyVec3, type Vec3 } from '@shared/math';
import { PmType, WeaponId, WeaponState, type PlayerState } from '@shared/types';
import { getWeaponDef } from '@shared/weapons/defs';
import { AIM_AUTOMELEE_RANGE, VIEWHEIGHT_STAND } from '@shared/constants';
import { TurnRateState, angleNormalize180, diffTrackAngle, fovScaleInv, fovTurnRateScale } from './look';

// ---- dvars (T6 PC defaults, research/data/dvars_t6mp.json / controller.json) ----------------
export const AIM = {
  slowdownRegionWidth: 90, slowdownRegionHeight: 90, //       aim_slowdown_region_*
  slowdownExtendedWidth: 90, slowdownExtendedHeight: 90, //    aim_slowdown_region_extended_*
  slowdownYawScale: 0.4, slowdownPitchScale: 0.4, //          aim_slowdown_*_scale
  slowdownYawScaleAds: 0.5, slowdownPitchScaleAds: 0.5, //    aim_slowdown_*_scale_ads
  autoAimLerp: 40, autoAimRegionWidth: 160, autoAimRegionHeight: 120, // aim_autoaim_*
  autoMeleeLerp: 40, autoMeleeRegionWidth: 320, autoMeleeRegionHeight: 240, // aim_automelee_*
  autoMeleeRange: AIM_AUTOMELEE_RANGE, //                       aim_automelee_range 100 (T6; T5 128)
  lockOnDeflection: 0.05, lockOnStrength: 0.6, lockOnPitchStrength: 0.6, // aim_lockon_* (sites 0x7027e4/0x702862)
  lockOnRegionWidth: 90, lockOnRegionHeight: 90,
  aimAssistRangeScale: 1, autoAimRangeScale: 1, //             aim_aimAssistRangeScale / aim_autoAimRangeScale
  minTargetDistance: 10000, //                                  aim_assist_min_target_distance
  sentientRadius: 10, //                                        aim_target_sentient_radius
  frustumMinDistance: 256, //                                   aim_target_frustum_min_distance (compared to dist^2, T5)
  /** T5 AimAssist_ApplyAutoMelee dist_offset. */
  meleeDistOffset: 32,
  /** ScrPlace_HiResGetScale(): ESTIMATE 1. */
  hiResScale: 1,
  maxTargets: 64,
} as const;

/**
 * Bone heights above the feet for a STANDING body, measured from the bind pose of
 * game/public/assets/models/body_seals_assault.glb (j_helmet 71.2, j_head 63.5, j_spine4 51.8).
 * Crouch/prone scale by viewHeight / VIEWHEIGHT_STAND. ESTIMATE (no per-frame skeleton in the input layer).
 */
export const BONE_HEIGHT = { helmet: 71.24, head: 63.52, spine4: 51.76 } as const;
function boneHeight(stand: number, viewHeight: number): number {
  const vh = viewHeight > 0 ? viewHeight : VIEWHEIGHT_STAND;
  return stand * (vh / VIEWHEIGHT_STAND);
}

/** A candidate target (interpolated remote player). */
export interface AimCandidate {
  clientNum: number;
  origin: ReadonlyVec3;
  velocity: ReadonlyVec3;
  viewHeight: number;
  alive: boolean;
}

export interface ScreenTarget {
  ent: number;
  /** Clip-space AABB: [x, y, z]. */
  clipMins: Vec3;
  clipMaxs: Vec3;
  aimPos: Vec3;
  velocity: Vec3;
  /** World distance^2 from the local player's origin (feet to feet). */
  distSqr: number;
  crosshairDistSqr: number;
}

export interface AimView {
  /** Eye position (refdef vieworg). */
  eye: ReadonlyVec3;
  /** Refdef angles. */
  angles: ReadonlyVec3;
  /** Current vertical fov, deg (ADS-lerped). */
  fovY: number;
  /** Viewport width / height in CSS px (region boxes are height-based). */
  screenW: number;
  screenH: number;
  /** cg_fov (settings.fov). */
  cgFov: number;
}

/** Local player facts aim assist needs (AimAssist_BackupPlayerState). */
export interface AimPlayer {
  origin: ReadonlyVec3;
  velocity: ReadonlyVec3;
  pmType: number;
  pmFlags: number;
  /** BG_GetViewmodelWeaponIndex: the offhand while throwing, else the held weapon. */
  weapon: WeaponId;
  weaponState: WeaponState;
  adsFrac: number;
  hasAmmo: boolean;
  /** weapFlags offhand-in-use. */
  offhandInUse: boolean;
}

/** Build AimPlayer from the predicted PlayerState. */
export function aimPlayerFromPs(ps: PlayerState, out?: AimPlayer): AimPlayer {
  const offhand = ps.offHandIndex !== WeaponId.NONE && ps.weaponState >= WeaponState.OFFHAND_INIT && ps.weaponState <= WeaponState.OFFHAND_END;
  const w = offhand ? ps.offHandIndex : ps.weapon;
  const o = out ?? ({} as AimPlayer);
  o.origin = ps.origin;
  o.velocity = ps.velocity;
  o.pmType = ps.pm_type;
  o.pmFlags = ps.pm_flags;
  o.weapon = w;
  o.weaponState = ps.weaponState;
  o.adsFrac = ps.adsFrac;
  o.hasAmmo = (ps.ammoClip[w] ?? 0) + (ps.ammoStock[w] ?? 0) > 0; // BG_WeaponAmmo > 0
  o.offhandInUse = offhand;
  return o;
}

/** Visibility test eye -> vis bone (world trace). Return true when nothing blocks. */
export type VisibilityFn = (from: ReadonlyVec3, to: ReadonlyVec3, targetEnt: number) => boolean;

/** Inputs for one gamepad view update. */
export interface AimPadInput {
  /** As CL_GamepadMove passes them: pitchAxis (+ = look down), yawAxis (+ = turn left), rightAxis (strafe). */
  pitchAxis: number;
  yawAxis: number;
  rightAxis: number;
  /** ADS bit of the current cmd state (holdADS XOR toggle). */
  ads: boolean;
  dt: number;
  sensitivity: number;
  altCurve: boolean;
  /** Settings: "target assist" (slowdown + lock-on) and "ADS auto-aim". */
  targetAssist: boolean;
  adsAutoAim: boolean;
  /** Lock-on enable (PC dvar 0, console ESTIMATE on with target assist) [CTL §5.3]. */
  lockOn: boolean;
}

const enum AMS { NOT_ACTIVE = 0, TARGET_ACQUIRED = 1, TARGETING = 2 }
const NO_ENT = -1;

const RELOAD_STATES: ReadonlySet<number> = new Set([
  WeaponState.RELOADING, WeaponState.RELOADING_INTERUPT, WeaponState.RELOAD_START, WeaponState.RELOAD_START_INTERUPT, WeaponState.RELOAD_END,
]);

/** AimTarget_PlayerInValidState: T5 rejects noclip/ufo/spectator/intermission (+ dead here). */
function playerValid(pmType: number): boolean {
  return !(pmType === PmType.NOCLIP || pmType === PmType.UFO || pmType === PmType.SPECTATOR || pmType === PmType.INTERMISSION
    || pmType === PmType.DEAD || pmType === PmType.DEAD_LINKED);
}

export class AimAssist {
  readonly targets: ScreenTarget[] = [];
  targetCount = 0;
  readonly turn = new TurnRateState();
  // view (refdef) used for the last screen-target update
  readonly viewOrigin: Vec3 = [0, 0, 0];
  readonly viewAngles: Vec3 = [0, 0, 0];
  private readonly fwd: Vec3 = [0, 0, 0];
  private readonly right: Vec3 = [0, 0, 0];
  private readonly up: Vec3 = [0, 0, 0];
  private tanHalfX = 1;
  private tanHalfY = 1;
  fovTurnRateScale = 1;
  private fovScaleInv = 1;
  // tweakables (clip-space half extents)
  private slowW = 0; private slowH = 0; private autoAimW = 0; private autoAimH = 0;
  private meleeW = 0; private meleeH = 0; private lockW = 0; private lockH = 0;
  private screenW = 1280; private screenH = 720;
  // player
  player: AimPlayer | null = null;
  // state
  slowdownActive = false;
  slowdownTargetEnt = NO_ENT;
  lockOnTargetEnt = NO_ENT;
  autoAimActive = false;
  autoAimTargetEnt = NO_ENT;
  private autoAimPressed = false;
  private autoAimJustGot = false;
  private autoAimPitch = 0; private autoAimYaw = 0; private autoAimPitchTarget = 0; private autoAimYawTarget = 0;
  autoMeleeState: number = AMS.NOT_ACTIVE;
  autoMeleeTargetEnt = NO_ENT;
  private autoMeleePitch = 0; private autoMeleeYaw = 0; private autoMeleePitchTarget = 0; private autoMeleeYawTarget = 0;
  private readonly tr: [number, number] = [0, 0];
  private readonly tmp: Vec3 = [0, 0, 0];
  private readonly ang: Vec3 = [0, 0, 0];
  private lastCandidates: readonly AimCandidate[] = [];

  /** AimAssist_Setup on spawn/teleport: drop every target. */
  reset(): void {
    this.targetCount = 0;
    this.turn.reset();
    this.slowdownActive = false; this.slowdownTargetEnt = NO_ENT; this.lockOnTargetEnt = NO_ENT;
    this.clearAutoAim(); this.autoAimPressed = false; this.clearAutoMelee();
  }

  get autoMeleeTargeting(): boolean { return this.autoMeleeState === AMS.TARGETING; }

  // =========================================================================================
  // targets
  // =========================================================================================
  /** AimTarget_UpdateClientTargets + AimAssist_UpdateScreenTargets + AimAssist_UpdateTweakables. */
  updateScreenTargets(view: AimView, player: AimPlayer, candidates: readonly AimCandidate[], localClientNum: number, visible: VisibilityFn | null): void {
    this.player = player;
    this.lastCandidates = candidates;
    this.viewOrigin[0] = view.eye[0]; this.viewOrigin[1] = view.eye[1]; this.viewOrigin[2] = view.eye[2];
    this.viewAngles[0] = view.angles[0]; this.viewAngles[1] = view.angles[1]; this.viewAngles[2] = view.angles[2];
    angleVectors(view.angles, this.fwd, this.right, this.up);
    this.screenW = Math.max(1, view.screenW); this.screenH = Math.max(1, view.screenH);
    this.tanHalfY = Math.tan(view.fovY * 0.5 * DEG2RAD);
    this.tanHalfX = this.tanHalfY * (this.screenW / this.screenH);
    this.fovTurnRateScale = fovTurnRateScale(view.fovY);
    this.fovScaleInv = fovScaleInv(view.fovY, view.cgFov);
    this.updateTweakables();
    this.targetCount = 0;
    if (!playerValid(player.pmType)) return;
    const maxD2 = AIM.minTargetDistance * AIM.minTargetDistance;
    for (const c of candidates) {
      if (c.clientNum === localClientNum || !c.alive) continue; // FFA: every other live player is an enemy
      const dx = c.origin[0] - player.origin[0], dy = c.origin[1] - player.origin[1], dz = c.origin[2] - player.origin[2];
      const distSqr = dx * dx + dy * dy + dz * dz;
      if (distSqr > maxD2) continue;
      // AimTarget_IsTargetValid: in front of the view plane (+radius)
      if (this.fwd[0] * dx + this.fwd[1] * dy + this.fwd[2] * dz + AIM.sentientRadius < 0) continue;
      // AimTarget_IsTargetVisible: trace eye -> vis bone (j_head, ESTIMATE)
      if (visible) {
        this.tmp[0] = c.origin[0]; this.tmp[1] = c.origin[1]; this.tmp[2] = c.origin[2] + boneHeight(BONE_HEIGHT.head, c.viewHeight);
        if (!visible(this.viewOrigin, this.tmp, c.clientNum)) continue;
      }
      const st = this.slot(this.targetCount);
      const top = boneHeight(BONE_HEIGHT.helmet, c.viewHeight); // aim_highest_bone j_helmet
      if (!this.convertToClipBounds(c.origin, top, st.clipMins, st.clipMaxs)) continue;
      st.ent = c.clientNum;
      st.velocity[0] = c.velocity[0]; st.velocity[1] = c.velocity[1]; st.velocity[2] = c.velocity[2];
      // AimAssist_CalcAimPos: aim_bone j_spine4
      st.aimPos[0] = c.origin[0]; st.aimPos[1] = c.origin[1]; st.aimPos[2] = c.origin[2] + boneHeight(BONE_HEIGHT.spine4, c.viewHeight);
      st.distSqr = distSqr;
      const cx = 0.5 * (st.clipMins[0] + st.clipMaxs[0]), cy = 0.5 * (st.clipMins[1] + st.clipMaxs[1]);
      st.crosshairDistSqr = cx * cx + cy * cy;
      this.insertSorted();
    }
  }

  private slot(i: number): ScreenTarget {
    let t = this.targets[i];
    if (!t) {
      t = { ent: NO_ENT, clipMins: [0, 0, 0], clipMaxs: [0, 0, 0], aimPos: [0, 0, 0], velocity: [0, 0, 0], distSqr: 0, crosshairDistSqr: 0 };
      this.targets[i] = t;
    }
    return t;
  }

  /** The candidate at targets[targetCount] is filled: insert it in crosshair order (AimAssist_AddToTargetList). */
  private insertSorted(): void {
    const n = this.targetCount;
    const t = this.targets[n];
    let i = n;
    while (i > 0 && this.targets[i - 1].crosshairDistSqr > t.crosshairDistSqr) { this.targets[i] = this.targets[i - 1]; i--; }
    this.targets[i] = t;
    if (this.targetCount < AIM.maxTargets) this.targetCount++;
  }

  /**
   * AimAssist_ConvertToClipBounds for the axis-aligned player box (T5 rotates by the entity angles;
   * the box is square around the origin, so only yaw matters and we ignore it, like a cylinder).
   */
  private convertToClipBounds(org: ReadonlyVec3, top: number, mins: Vec3, maxs: Vec3): boolean {
    mins[0] = mins[1] = mins[2] = Infinity;
    maxs[0] = maxs[1] = maxs[2] = -Infinity;
    const r = AIM.sentientRadius;
    for (let i = 0; i < 8; i++) {
      const px = org[0] + (i & 1 ? r : -r) - this.viewOrigin[0];
      const py = org[1] + (i & 2 ? r : -r) - this.viewOrigin[1];
      const pz = org[2] + (i & 4 ? top : 0) - this.viewOrigin[2];
      const z = px * this.fwd[0] + py * this.fwd[1] + pz * this.fwd[2];
      if (z <= 0) continue; // clip w <= 0
      const x = (px * this.right[0] + py * this.right[1] + pz * this.right[2]) / (z * this.tanHalfX);
      const y = (px * this.up[0] + py * this.up[1] + pz * this.up[2]) / (z * this.tanHalfY);
      const zc = 1 - 1 / z; // infinite perspective, near = 1
      if (x < mins[0]) mins[0] = x; if (x > maxs[0]) maxs[0] = x;
      if (y < mins[1]) mins[1] = y; if (y > maxs[1]) maxs[1] = y;
      if (zc < mins[2]) mins[2] = zc; if (zc > maxs[2]) maxs[2] = zc;
    }
    if (mins[0] >= maxs[0] || mins[1] >= maxs[1] || mins[2] >= maxs[2]) return false;
    if (mins[0] > 1 || mins[1] > 1 || mins[2] > 1) return false;
    if (maxs[0] < -1 || maxs[1] < -1 || maxs[2] < 0) return false;
    for (let k = 0; k < 2; k++) {
      mins[k] = Math.max(-1, Math.min(1, mins[k])); maxs[k] = Math.max(-1, Math.min(1, maxs[k]));
    }
    mins[2] = Math.max(0, Math.min(1, mins[2])); maxs[2] = Math.max(0, Math.min(1, maxs[2]));
    return true;
  }

  /** AimAssist_UpdateTweakables: virtual 640x480 region sizes -> clip half extents (height-based scale). */
  private updateTweakables(): void {
    const sx = this.screenH / 480; // scaleVirtualToReal (height based, wide screens get a narrower box)
    const w = (v: number) => (v * AIM.hiResScale * sx) / this.screenW;
    const h = (v: number) => (v * AIM.hiResScale * sx) / this.screenH;
    if (this.slowdownActive) { this.slowW = w(AIM.slowdownExtendedWidth); this.slowH = h(AIM.slowdownExtendedHeight); }
    else { this.slowW = w(AIM.slowdownRegionWidth); this.slowH = h(AIM.slowdownRegionHeight); }
    this.autoAimW = w(AIM.autoAimRegionWidth) * this.fovScaleInv; this.autoAimH = h(AIM.autoAimRegionHeight) * this.fovScaleInv;
    this.meleeW = w(AIM.autoMeleeRegionWidth) * this.fovScaleInv; this.meleeH = h(AIM.autoMeleeRegionHeight) * this.fovScaleInv;
    this.lockW = w(AIM.lockOnRegionWidth); this.lockH = h(AIM.lockOnRegionHeight);
  }

  /** AimAssist_GetBestTarget: first in crosshair order within range that overlaps the centre box. */
  bestTarget(range: number, hw: number, hh: number): ScreenTarget | null {
    const r2 = range * range;
    for (let i = 0; i < this.targetCount; i++) {
      const t = this.targets[i];
      if (t.distSqr <= r2 && intersectsCenterBox(t, hw, hh)) return t;
    }
    return null;
  }
  targetFromEntity(ent: number): ScreenTarget | null {
    if (ent === NO_ENT) return null;
    for (let i = 0; i < this.targetCount; i++) if (this.targets[i].ent === ent) return this.targets[i];
    return null;
  }

  // =========================================================================================
  // weapon facts
  // =========================================================================================
  /** AimAssist_GetAimAssistRange: lerp(aimAssistRange, aimAssistRangeAds, adsLerp) * scale. */
  aimAssistRange(): number {
    const p = this.player;
    if (!p || p.weapon === WeaponId.NONE) return 0;
    const d = getWeaponDef(p.weapon);
    const a = clamp01(p.adsFrac);
    return ((1 - a) * d.aimAssistRange + d.aimAssistRangeAds * a) * AIM.aimAssistRangeScale;
  }
  private requireLockonToFire(): boolean {
    const p = this.player;
    if (!p) return false;
    const r = getWeaponDef(p.weapon).raw['requireLockonToFire'];
    return typeof r === 'number' && r !== 0;
  }
  /** AimAssist_IsPlayerUsingOffhand: offhand in use and its impactType is not a blade. */
  private usingOffhand(): boolean {
    const p = this.player;
    if (!p || !p.offhandInUse || p.weapon === WeaponId.NONE) return false;
    return getWeaponDef(p.weapon).raw['impactType'] !== 'blade';
  }
  /** AimAssist_IsSlowdownActive. */
  private slowdownAllowed(targetAssist: boolean): boolean {
    const p = this.player;
    if (!p || !targetAssist || p.weapon === WeaponId.NONE) return false;
    if (this.requireLockonToFire()) return false;
    // CTL §5.2: "(ADS OR weapon has ammo)": only an empty weapon at the hip loses slowdown. ADS = the ADS lerp
    // (ps weaponPosFrac / adsFrac) is above 0.
    return p.adsFrac > 0 || p.hasAmmo;
  }

  // =========================================================================================
  // gamepad (AimAssist_UpdateGamePadInput) — returns view deltas [dPitch, dYaw]
  // =========================================================================================
  updateGamePad(out: [number, number], i: AimPadInput): [number, number] {
    const p = this.player;
    let dPitch = 0, dYaw = 0;
    // 1. AimAssist_ApplyTurnRates (includes AimAssist_CalcSlowdown)
    let slowP = 1, slowY = 1;
    this.slowdownActive = false;
    this.slowdownTargetEnt = NO_ENT;
    if (!this.autoMeleeTargeting && this.slowdownAllowed(i.targetAssist)) {
      const t = this.bestTarget(this.aimAssistRange(), this.slowW, this.slowH);
      if (t) {
        const a = clamp01(p!.adsFrac);
        slowP = AIM.slowdownPitchScale + (AIM.slowdownPitchScaleAds - AIM.slowdownPitchScale) * a;
        slowY = AIM.slowdownYawScale + (AIM.slowdownYawScaleAds - AIM.slowdownYawScale) * a;
        this.slowdownActive = true;
        this.slowdownTargetEnt = t.ent;
      }
      if (this.usingOffhand()) slowP = 1;
    }
    this.turn.apply(this.tr, {
      pitchAxis: i.pitchAxis, yawAxis: i.yawAxis, adsLerp: p ? p.adsFrac : 0, fovScale: this.fovTurnRateScale,
      sensitivity: i.sensitivity, slowPitch: slowP, slowYaw: slowY, dt: i.dt, autoMeleeTargeting: this.autoMeleeTargeting,
      altCurve: i.altCurve,
    });
    dPitch += this.tr[0]; dYaw += this.tr[1];
    // 2..4 (all skipped in T5 weapon states 47-49 = low-ready, which SnS never uses)
    this.applyAutoAim(this.tr, i);
    dPitch += this.tr[0]; dYaw += this.tr[1];
    this.trackAutoMelee(this.tr, i.dt);
    dPitch += this.tr[0]; dYaw += this.tr[1];
    this.applyLockOn(this.tr, i);
    dPitch += this.tr[0]; dYaw += this.tr[1];
    out[0] = dPitch; out[1] = dYaw;
    return out;
  }

  /** AimAssist_UpdateMouseInput: auto-melee only [CTL §5]. */
  updateMouse(out: [number, number], dt: number): [number, number] {
    this.slowdownActive = false;
    this.lockOnTargetEnt = NO_ENT;
    this.turn.reset();
    this.trackAutoMelee(out, dt);
    return out;
  }

  // ---- auto-aim (ADS snap) -------------------------------------------------------------------
  private clearAutoAim(): void {
    this.autoAimTargetEnt = NO_ENT; this.autoAimActive = false; this.autoAimJustGot = false;
    this.autoAimPitch = this.autoAimPitchTarget = this.autoAimYaw = this.autoAimYawTarget = 0;
  }
  private updateAutoAimTarget(): boolean {
    const t = this.targetFromEntity(this.autoAimTargetEnt);
    if (!t) return false;
    this.anglesTo(t.aimPos);
    this.autoAimPitchTarget = this.ang[0];
    this.autoAimYawTarget = this.ang[1];
    return true;
  }
  private applyAutoAim(out: [number, number], i: AimPadInput): void {
    out[0] = 0; out[1] = 0;
    const p = this.player;
    if (!(i.adsAutoAim && i.ads && p && p.weapon !== WeaponId.NONE)) {
      this.clearAutoAim();
      this.autoAimPressed = false;
      return;
    }
    if (!this.autoAimPressed) {
      const def = getWeaponDef(p.weapon);
      if (!RELOAD_STATES.has(p.weaponState) && def.aimDownSight) { // BG_IsAimDownSightWeapon
        const t = this.bestTarget(def.autoAimRange * AIM.autoAimRangeScale, this.autoAimW, this.autoAimH);
        if (t) {
          this.clearAutoAim();
          this.autoAimTargetEnt = t.ent;
          this.autoAimActive = true;
          this.autoAimPitch = this.viewAngles[0];
          this.autoAimYaw = this.viewAngles[1];
          this.updateAutoAimTarget();
          this.autoAimJustGot = true;
        }
      }
      this.autoAimPressed = true;
    }
    if (!this.autoAimActive) return;
    if (p.adsFrac < 1 && !this.updateAutoAimTarget() && !this.autoAimJustGot) { this.clearAutoAim(); return; }
    const np = diffTrackAngle(this.autoAimPitchTarget, this.autoAimPitch, AIM.autoAimLerp, i.dt);
    const ny = diffTrackAngle(this.autoAimYawTarget, this.autoAimYaw, AIM.autoAimLerp, i.dt);
    const dp = angleNormalize180(np - this.autoAimPitch);
    const dy = angleNormalize180(ny - this.autoAimYaw);
    this.autoAimPitch = np; this.autoAimYaw = ny;
    out[0] = dp; out[1] = dy;
    if (Math.abs(dp) < 0.001 && Math.abs(dy) <= 0.001 && p.adsFrac === 0) this.clearAutoAim();
  }

  // ---- auto-melee ----------------------------------------------------------------------------
  private clearAutoMelee(): void {
    this.autoMeleeState = AMS.NOT_ACTIVE; this.autoMeleeTargetEnt = NO_ENT;
    this.autoMeleePitch = this.autoMeleePitchTarget = this.autoMeleeYaw = this.autoMeleeYawTarget = 0;
  }
  /** Lunge range: weapon meleeChargeRange, else aim_automelee_range (T6 AA_GetAutoMeleeRange, ESTIMATE same rule). */
  autoMeleeRange(): number {
    const p = this.player;
    if (!p || p.weapon === WeaponId.NONE) return 0;
    const r = getWeaponDef(p.weapon).meleeChargeRange;
    return r !== 0 ? r : AIM.autoMeleeRange;
  }
  /**
   * AimAssist_ApplyAutoMelee, press-edge part: run once per usercmd with that cmd's MELEE bit
   * (edge vs the previous cmd's). Fills the lunge yaw (deg) / dist (0..255) for the cmd, or dist 0.
   */
  autoMeleeOnCmd(meleeDown: boolean, prevMeleeDown: boolean, out: { yaw: number; dist: number }): void {
    out.yaw = 0; out.dist = 0;
    const p = this.player;
    if (!p || p.weapon === WeaponId.NONE) { this.clearAutoMelee(); return; } // aim_automelee_enabled = 1
    const meleeing = meleeDown && !prevMeleeDown;
    if (this.autoMeleeState === AMS.TARGETING || !meleeing) return;
    let t = this.targetFromEntity(this.autoMeleeTargetEnt);
    if (!t) t = this.bestTarget(this.autoMeleeRange(), this.meleeW, this.meleeH);
    if (!t) { this.clearAutoMelee(); return; }
    // AimAssist_SetAutoMeleeTarget
    this.autoMeleeTargetEnt = t.ent;
    this.autoMeleeState = AMS.TARGET_ACQUIRED;
    this.autoMeleePitch = this.viewAngles[0]; this.autoMeleePitchTarget = 0;
    this.autoMeleeYaw = this.viewAngles[1]; this.autoMeleeYawTarget = 0;
    if ((p.pmFlags & 1) === 0 && t.distSqr > 0) { // not PMF_PRONE
      let dist = Math.sqrt(t.distSqr) - AIM.meleeDistOffset;
      if (dist > 0) {
        if (dist > 255) dist = 255;
        out.yaw = Math.atan2(t.aimPos[1] - this.viewOrigin[1], t.aimPos[0] - this.viewOrigin[0]) * RAD2DEG; // vectoyaw
        if (out.yaw < 0) out.yaw += 360;
        out.dist = Math.trunc(dist);
      }
    }
  }
  /** AimAssist_ApplyAutoMelee, per-frame part: state advance + view pull (DiffTrackAngle at lerp 40). */
  private trackAutoMelee(out: [number, number], dt: number): void {
    out[0] = 0; out[1] = 0;
    const p = this.player;
    if (!p || p.weapon === WeaponId.NONE) { this.clearAutoMelee(); return; }
    if (this.autoMeleeState === AMS.TARGET_ACQUIRED && p.weaponState === WeaponState.MELEE_INIT) this.autoMeleeState = AMS.TARGETING;
    if (this.autoMeleeState !== AMS.TARGETING) return;
    // T5: tracks during MELEE_INIT (17) and MELEE_END (19), clears otherwise (incl. MELEE_FIRE 18)
    if ((p.weaponState === WeaponState.MELEE_INIT || p.weaponState === WeaponState.MELEE_END) && this.updateAutoMeleeTarget()) {
      const np = diffTrackAngle(this.autoMeleePitchTarget, this.autoMeleePitch, AIM.autoMeleeLerp, dt);
      const ny = diffTrackAngle(this.autoMeleeYawTarget, this.autoMeleeYaw, AIM.autoMeleeLerp, dt);
      out[0] = angleNormalize180(np - this.autoMeleePitch);
      out[1] = angleNormalize180(ny - this.autoMeleeYaw);
      this.autoMeleePitch = np; this.autoMeleeYaw = ny;
    } else {
      this.clearAutoMelee();
    }
  }
  private updateAutoMeleeTarget(): boolean {
    let pos: ReadonlyVec3 | null = null;
    const t = this.targetFromEntity(this.autoMeleeTargetEnt);
    if (t) pos = t.aimPos;
    else {
      if (this.autoMeleeTargetEnt === NO_ENT) return false;
      // off the screen-target list (e.g. behind a corner): use the entity directly (AimAssist_CalcAimPos)
      const c = this.lastCandidates.find((x) => x.clientNum === this.autoMeleeTargetEnt);
      if (!c || !c.alive) return false;
      this.tmp[0] = c.origin[0]; this.tmp[1] = c.origin[1]; this.tmp[2] = c.origin[2] + boneHeight(BONE_HEIGHT.spine4, c.viewHeight);
      pos = this.tmp;
    }
    this.anglesTo(pos);
    this.autoMeleePitchTarget = this.ang[0];
    this.autoMeleeYawTarget = this.ang[1];
    return true;
  }

  // ---- lock-on (rotational tracking) ---------------------------------------------------------
  private applyLockOn(out: [number, number], i: AimPadInput): void {
    out[0] = 0; out[1] = 0;
    const prev = this.lockOnTargetEnt;
    this.lockOnTargetEnt = NO_ENT;
    const p = this.player;
    if (!(i.lockOn && i.targetAssist && p && !this.usingOffhand() && !this.autoAimActive && !this.autoMeleeTargeting && !this.requireLockonToFire())) return;
    if (!(AIM.lockOnDeflection <= Math.abs(i.pitchAxis) || AIM.lockOnDeflection <= Math.abs(i.yawAxis) || AIM.lockOnDeflection <= Math.abs(i.rightAxis))) return;
    if (p.weapon === WeaponId.NONE) return;
    const range = this.aimAssistRange();
    // AimAssist_GetPrevOrBestTarget
    let t = this.targetFromEntity(prev);
    if (!(t && range * range > t.distSqr && intersectsCenterBox(t, this.lockW, this.lockH))) t = this.bestTarget(range, this.lockW, this.lockH);
    if (!t || t.distSqr <= 0) return;
    this.lockOnTargetEnt = t.ent;
    const arc = Math.sqrt(t.distSqr) * Math.PI;
    const rvx = t.velocity[0] - p.velocity[0], rvy = t.velocity[1] - p.velocity[1], rvz = t.velocity[2] - p.velocity[2];
    // viewAxis[1] = left = -right, viewAxis[2] = up
    const relLeft = -(rvx * this.right[0] + rvy * this.right[1] + rvz * this.right[2]);
    const relUp = rvx * this.up[0] + rvy * this.up[1] + rvz * this.up[2];
    const pitchTurnRate = (relUp / arc) * 180 * AIM.lockOnPitchStrength;
    out[1] = (relLeft / arc) * 180 * AIM.lockOnStrength * i.dt;
    out[0] = -pitchTurnRate * i.dt;
  }

  /** vectoangles(target - viewOrigin) into this.ang ([pitch (down +), yaw]). */
  private anglesTo(pos: ReadonlyVec3): void {
    const dx = pos[0] - this.viewOrigin[0], dy = pos[1] - this.viewOrigin[1], dz = pos[2] - this.viewOrigin[2];
    let yaw = 0, pitch = 0;
    if (dx === 0 && dy === 0) { yaw = 0; pitch = dz > 0 ? 270 : 90; }
    else {
      yaw = Math.atan2(dy, dx) * RAD2DEG; if (yaw < 0) yaw += 360;
      const fwd = Math.sqrt(dx * dx + dy * dy);
      pitch = Math.atan2(dz, fwd) * RAD2DEG;
      if (pitch < 0) pitch += 360;
      pitch = -pitch;
    }
    this.ang[0] = pitch; this.ang[1] = yaw; this.ang[2] = 0;
  }
}

/** AimAssist_DoBoundsIntersectCenterBox. */
export function intersectsCenterBox(t: ScreenTarget, hw: number, hh: number): boolean {
  return hw >= t.clipMins[0] && t.clipMaxs[0] >= -hw && hh >= t.clipMins[1] && t.clipMaxs[1] >= -hh;
}

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v; }
